import {
  DndContext,
  DragOverlay,
  PointerSensor,
  pointerWithin,
  rectIntersection,
  useSensor,
  useSensors,
  type CollisionDetection,
  type DragEndEvent,
  type DragMoveEvent,
  type DragStartEvent
} from "@dnd-kit/core";
import { toast } from "sonner";
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type FC,
  type ReactNode
} from "react";

import { FilesystemAPI } from "../../caseFiles/api/filesystemApi";
import { nodeTitle } from "../../caseFiles/nodeNames";
import type { FsNode } from "../../caseFiles/types";
import { useCaseIde } from "../context/CaseIdeContext";
import { useCaseData } from "../data/CaseDataContext";
import {
  canDropInto,
  placementFor,
  type DropPlacement,
  type TreeNodeLike,
  type TreeRow
} from "../files/treeRows";
import { WorkspaceTreeAPI } from "../workspace/api/workspaceTreeApi";
import { draftTitle, formName } from "../workspace/workspaceModel";
import { MoveToWorkProductsDialog } from "./MoveToWorkProductsDialog";
import {
  copyableWorkProducts,
  inTreeOrder,
  isMovableDocx,
  moveWarning,
  ONLY_DOCX_TOAST,
  positionOf,
  promoteRequestFor,
  rejectCopyMessage,
  type ExplorerTree,
  type MoveWarning,
  type WorkspaceDragItem
} from "./crossDrop";

type DragRow = TreeRow & { expanded: boolean };

type DropPayload = {
  tree: ExplorerTree;
  root?: boolean;
  row?: DragRow;
};

type DragPayload = {
  tree: ExplorerTree;
  nodeId: string;
};

export type TreeRegistration = {
  selection: () => ReadonlySet<string>;
  expand: (parentId: string) => void;
  cancelRename: () => void;
};

type Hint = {
  tree: ExplorerTree;
  /** Null when the drop is the empty part of a pane. */
  id: string | null;
  placement: DropPlacement;
};

type DragSession = { tree: ExplorerTree; ids: string[] };

type PendingMove = {
  files: FsNode[];
  warning: MoveWarning;
  placement: DropPlacement;
};

type ExplorerDndValue = {
  registerTree: (tree: ExplorerTree, hooks: TreeRegistration) => () => void;
  isDragging: (nodeId: string) => boolean;
  dropHintFor: (tree: ExplorerTree, nodeId: string) => DropPlacement | null;
  isRootTarget: (tree: ExplorerTree) => boolean;
};

const ExplorerDndContext = createContext<ExplorerDndValue | null>(null);

const FALLBACK: ExplorerDndValue = {
  registerTree: () => () => undefined,
  isDragging: () => false,
  dropHintFor: () => null,
  isRootTarget: () => false
};

/** Drag state for both panes. Outside the provider (unit tests) drags are inert. */
export const useExplorerDnd = (): ExplorerDndValue =>
  useContext(ExplorerDndContext) ?? FALLBACK;

const preferRow: CollisionDetection = (args) => {
  const hits = pointerWithin(args);
  const rows = hits.filter((hit) => !String(hit.id).endsWith(":root"));
  if (rows.length) return rows;
  if (hits.length) return hits;
  const rects = rectIntersection(args);
  const rowRects = rects.filter((hit) => !String(hit.id).endsWith(":root"));
  return rowRects.length ? rowRects : rects;
};

const messageOf = (error: unknown, fallback: string) =>
  error instanceof Error && error.message ? error.message : fallback;

const workspaceLabel = (
  ws: ReturnType<typeof useCaseData>["workspace"],
  nodeId: string
): string => {
  const node = ws.tree.nodes.find((entry) => entry.id === nodeId);
  if (!node) return "1 item";
  if (node.kind === "folder") return node.name || "Folder";
  if (node.kind === "draft") {
    const draft = ws.drafts.find((entry) => entry.draft_id === node.ref_id);
    return draft ? draftTitle(draft) : "Draft";
  }
  if (node.kind === "form") {
    const form = ws.forms.find(
      (entry) => entry.case_extend_uscis_filled_form_id === node.ref_id
    );
    return form ? formName(form) : "Form";
  }
  return node.name || "1 item";
};

/**
 * One drag context for Case Files and Work Products, so a document can cross
 * the divider. Reordering inside a pane is unchanged.
 */
export const ExplorerDnd: FC<{ children: ReactNode }> = ({ children }) => {
  const { individualId, open, invalidate } = useCaseIde();
  const { fs, workspace } = useCaseData();
  const trees = useRef<Partial<Record<ExplorerTree, TreeRegistration>>>({});
  const model = useRef({ fs, workspace, open, invalidate });
  model.current = { fs, workspace, open, invalidate };

  const [drag, setDrag] = useState<DragSession | null>(null);
  const [hint, setHint] = useState<Hint | null>(null);
  const dragRef = useRef(drag);
  const hintRef = useRef(hint);
  dragRef.current = drag;
  hintRef.current = hint;
  const [pending, setPending] = useState<PendingMove | null>(null);
  const [saving, setSaving] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } })
  );

  const registerTree = useCallback(
    (tree: ExplorerTree, hooks: TreeRegistration) => {
      trees.current[tree] = hooks;
      return () => {
        if (trees.current[tree] === hooks) delete trees.current[tree];
      };
    },
    []
  );

  const clearDrag = () => {
    dragRef.current = null;
    hintRef.current = null;
    setDrag(null);
    setHint(null);
  };

  const onDragStart = (event: DragStartEvent) => {
    const data = event.active.data.current as DragPayload | undefined;
    if (!data) return;
    trees.current[data.tree]?.cancelRename();
    const selected = trees.current[data.tree]?.selection() ?? new Set<string>();
    const ids = selected.has(data.nodeId) ? [...selected] : [data.nodeId];
    const next = { tree: data.tree, ids };
    dragRef.current = next;
    setDrag(next);
  };

  const onDragMove = (event: DragMoveEvent) => {
    const session = dragRef.current;
    const rect = event.active.rect.current.translated;
    const { over } = event;
    if (!session || !over || !rect) {
      hintRef.current = null;
      setHint(null);
      return;
    }
    const data = over.data.current as DropPayload | undefined;
    if (!data) {
      hintRef.current = null;
      setHint(null);
      return;
    }
    let placement: DropPlacement;
    let id: string | null = null;
    if (data.root || !data.row) {
      placement = { kind: "inside", parentId: null };
    } else {
      if (
        session.tree === data.tree &&
        session.ids.includes(data.row.node.id)
      ) {
        hintRef.current = null;
        setHint(null);
        return;
      }
      const centerY = rect.top + rect.height / 2;
      const ratio = Math.min(
        1,
        Math.max(0, (centerY - over.rect.top) / over.rect.height)
      );
      placement = placementFor(data.row, ratio);
      id = data.row.node.id;
    }
    if (session.tree === data.tree) {
      const nodes: TreeNodeLike[] =
        session.tree === "files"
          ? model.current.fs.nodes
          : model.current.workspace.tree.nodes;
      if (!canDropInto(nodes, session.ids, placement.parentId)) {
        hintRef.current = null;
        setHint(null);
        return;
      }
    }
    const next = { tree: data.tree, id, placement };
    hintRef.current = next;
    setHint(next);
  };

  const copyToCaseFiles = async (ids: string[], placement: DropPlacement) => {
    const { workspace: ws, fs: files, invalidate: refresh } = model.current;
    const ordered = inTreeOrder(ids, ws.tree.nodes);
    const items: WorkspaceDragItem[] = ordered.map((id) => {
      const node = ws.tree.nodes.find((entry) => entry.id === id);
      if (!node || node.kind === "folder") {
        return {
          nodeId: id,
          kind: node?.kind ?? "folder",
          refId: null,
          title: node?.name ?? "",
          copyable: false
        };
      }
      if (node.kind === "draft") {
        const draft = ws.drafts.find((entry) => entry.draft_id === node.ref_id);
        return {
          nodeId: id,
          kind: "draft",
          refId: node.ref_id,
          title: draft ? draftTitle(draft) : "",
          copyable: Boolean(draft?.docx_storage_path)
        };
      }
      if (node.kind === "form") {
        const form = ws.forms.find(
          (entry) => entry.case_extend_uscis_filled_form_id === node.ref_id
        );
        return {
          nodeId: id,
          kind: "form",
          refId: node.ref_id,
          title: form ? formName(form) : "",
          copyable: Boolean(form)
        };
      }
      return {
        nodeId: id,
        kind: node.kind,
        refId: node.ref_id,
        title: node.name,
        copyable: false
      };
    });
    const { sources, rejected } = copyableWorkProducts(items);
    if (!sources.length) {
      toast.error(rejectCopyMessage(rejected));
      return;
    }
    const created: string[] = [];
    let unresolved = 0;
    const undo = () => {
      void files
        .remove(created)
        .then(() => refresh(["fs", "packet"]))
        .catch(() => toast.error("Couldn't remove the copy."));
    };
    try {
      const promoted = await sources.reduce(async (previous, source) => {
        const soFar = await previous;
        const result = await FilesystemAPI.promote(
          individualId,
          promoteRequestFor(source, placement.parentId)
        );
        return {
          ids: [...soFar.ids, result.node.id],
          unresolved: soFar.unresolved + (result.unresolved_citations ?? 0)
        };
      }, Promise.resolve({ ids: [] as string[], unresolved: 0 }));
      created.push(...promoted.ids);
      unresolved = promoted.unresolved;
      if (placement.kind !== "inside") {
        await FilesystemAPI.moveNodes(
          individualId,
          created,
          placement.parentId,
          {
            beforeId: placement.kind === "before" ? placement.siblingId : null,
            afterId: placement.kind === "after" ? placement.siblingId : null
          }
        );
      }
      if (placement.parentId) trees.current.files?.expand(placement.parentId);
      await files.refresh();
      refresh(["fs", "packet"]);
      const label =
        sources.length === 1
          ? `Added ${sources[0].title} to Case Files`
          : `Added ${sources.length} documents to Case Files`;
      toast.success(label, {
        description: rejected.length
          ? rejectCopyMessage(rejected)
          : "Included in the packet.",
        action: { label: "Undo", onClick: undo }
      });
      if (unresolved) {
        toast.warning(
          `${unresolved} citation${
            unresolved === 1 ? "" : "s"
          } in the copy point${
            unresolved === 1 ? "s" : ""
          } at exhibits no longer in the packet.`,
          { description: "They print as “[exhibit removed]”." }
        );
      }
    } catch (error) {
      if (created.length) {
        await files.refresh();
        refresh(["fs", "packet"]);
      }
      toast.error(messageOf(error, "Couldn't add it to Case Files."), {
        action: created.length ? { label: "Undo", onClick: undo } : undefined
      });
    }
  };

  const onDragEnd = (event: DragEndEvent) => {
    const session = dragRef.current;
    const drop = hintRef.current;
    clearDrag();
    if (!session || !drop || !event.over) return;
    const target = positionOf(drop.placement);
    if (session.tree === drop.tree) {
      const move =
        session.tree === "files"
          ? model.current.fs.move
          : model.current.workspace.tree.move;
      void move(session.ids, target);
      if (drop.placement.parentId) {
        trees.current[session.tree]?.expand(drop.placement.parentId);
      }
      return;
    }
    if (session.tree === "files" && drop.tree === "workspace") {
      const ordered = inTreeOrder(session.ids, model.current.fs.nodes);
      const dragged = ordered
        .map((id) => model.current.fs.nodes.find((node) => node.id === id))
        .filter((node): node is FsNode => Boolean(node));
      const moving = dragged.filter(isMovableDocx);
      if (!moving.length) {
        toast.error(ONLY_DOCX_TOAST);
        return;
      }
      setPending({
        files: moving,
        warning: moveWarning(moving, dragged),
        placement: drop.placement
      });
      return;
    }
    if (session.tree === "workspace" && drop.tree === "files") {
      void copyToCaseFiles(session.ids, drop.placement);
    }
  };

  const confirmMove = async () => {
    if (!pending || saving) return;
    setSaving(true);
    const {
      fs: files,
      workspace: ws,
      open: openArtifact,
      invalidate: refresh
    } = model.current;
    try {
      const target = positionOf(pending.placement);
      const result = await WorkspaceTreeAPI.fromCaseFiles(individualId, {
        nodeIds: pending.files.map((node) => node.id),
        parentId: target.target_parent_id,
        beforeId: target.before_id,
        afterId: target.after_id
      });
      if (pending.placement.parentId) {
        trees.current.workspace?.expand(pending.placement.parentId);
      }
      await files.refresh();
      await ws.tree.refresh();
      refresh(["fs", "drafts", "packet"]);
      if (result.moved.length === 1) {
        const item = result.moved[0];
        openArtifact({
          kind: "draft",
          id: item.draft.draft_id,
          title: item.draft.title?.trim() || nodeTitle(pending.files[0])
        });
      }
      toast.success(
        result.moved.length === 1
          ? `Moved ${
              result.moved[0].draft.title?.trim() || "the document"
            } to Work Products`
          : `Moved ${result.moved.length} documents to Work Products`
      );
      setPending(null);
    } catch (error) {
      toast.error(messageOf(error, "Couldn't move it to Work Products."));
    } finally {
      setSaving(false);
    }
  };

  const overlay =
    drag == null
      ? null
      : drag.ids.length === 1
      ? drag.tree === "files"
        ? nodeTitle(
            model.current.fs.nodes.find((node) => node.id === drag.ids[0]) ?? {
              name: "1 item"
            }
          )
        : workspaceLabel(model.current.workspace, drag.ids[0])
      : `${drag.ids.length} items`;

  const value = useMemo<ExplorerDndValue>(
    () => ({
      registerTree,
      isDragging: (nodeId) => drag?.ids.includes(nodeId) ?? false,
      dropHintFor: (tree, nodeId) =>
        hint?.tree === tree && hint.id === nodeId ? hint.placement : null,
      isRootTarget: (tree) => hint?.tree === tree && hint.id === null
    }),
    [registerTree, drag, hint]
  );

  return (
    <ExplorerDndContext.Provider value={value}>
      <div className="flex min-h-0 flex-1 flex-col">
        <DndContext
          sensors={sensors}
          collisionDetection={preferRow}
          onDragStart={onDragStart}
          onDragMove={onDragMove}
          onDragEnd={onDragEnd}
          onDragCancel={clearDrag}
        >
          {children}
          <DragOverlay dropAnimation={null}>
            {overlay ? (
              <div className="rounded-md bg-elevated px-2 py-1 text-sm text-fg shadow-md ring-1 ring-line-2">
                {overlay}
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      </div>
      <MoveToWorkProductsDialog
        warning={pending?.warning ?? null}
        pending={saving}
        onOpenChange={(open) => {
          if (!open && !saving) setPending(null);
        }}
        onConfirm={() => void confirmMove()}
      />
    </ExplorerDndContext.Provider>
  );
};
